import { secondAxios } from "../../../../../../../api/axios";
import { projectTypeDiscipline } from "../../../../../../../constants/projectTypes";
import abbreviationList from "./abbreviation.json";

export const documentAbbreviations = Array.isArray(abbreviationList)
  ? abbreviationList
  : [];

// Keyed upper case: a code is matched however it was typed or stored. The
// catalog spells some of them mixed case ("Dwg", "ESum") while a stored UID
// may have been upper-cased on the way through.
const abbreviationByCode = new Map(
  documentAbbreviations.map((item) => [
    String(item.abbreviation ?? "")
      .trim()
      .toUpperCase(),
    item,
  ]),
);

const lookupAbbreviation = (code) =>
  abbreviationByCode.get(String(code ?? "").trim().toUpperCase());

// The catalog's own spelling of a code ("DWG" -> "Dwg"), or the value as
// given when it names nothing in the catalog - rows written under an older
// list keep reading as they did.
const canonicalAbbreviation = (value) => {
  const raw = String(value ?? "").trim();
  return lookupAbbreviation(raw)?.abbreviation ?? raw;
};

const abbreviationByDescription = new Map(
  documentAbbreviations.map((item) => [
    String(item.description ?? "")
      .trim()
      .toUpperCase(),
    String(item.abbreviation ?? "").trim(),
  ]),
);

/** Parse stored values like COR-001 → { abbr: "COR", seq: "001" }. */
export const parseNumberedDocumentUid = (raw) => {
  const value = String(raw ?? "").trim();
  // Digits allowed inside the code itself ("3D-001"), not only letters.
  const match = value.match(/^([A-Za-z0-9]+)-(\d+)$/);
  if (!match) return null;
  // Canonical rather than upper-cased, so the code read back out of a UID
  // matches the catalog entry it came from and numbering stays on the same
  // sequence instead of restarting.
  return { abbr: canonicalAbbreviation(match[1]), seq: match[2] };
};

/** Map stored documentName to abbreviation code when possible. */
export const resolveDocumentNameAbbreviation = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const numbered = parseNumberedDocumentUid(value);
  if (numbered) return numbered.abbr;
  if (lookupAbbreviation(value)) return canonicalAbbreviation(value);
  const fromDescription = abbreviationByDescription.get(value.toUpperCase());
  return fromDescription ?? value;
};

/** Resolve stored documentName / abbr to the full identifier description. */
export const resolveDocumentNameDescription = (raw) => {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const abbr = resolveDocumentNameAbbreviation(value);
  const known = lookupAbbreviation(abbr);
  if (known?.description) return String(known.description).trim();
  if (abbreviationByDescription.has(value.toUpperCase())) return value;
  return value;
};

/** Prefer abbreviation codes (no spaces); map descriptions to codes. */
const toUidAbbreviation = (documentNameOrAbbr) => {
  const raw = String(documentNameOrAbbr ?? "").trim();
  if (!raw) return "";
  const resolved = resolveDocumentNameAbbreviation(raw);
  if (!resolved || /\s/.test(resolved)) return "";
  return resolved;
};

/**
 * UID from full document abbreviation + sequence (e.g. PRC + 101 → PRC-101).
 */
export const buildDocumentUid = (documentNameOrAbbr, sequence) => {
  const abbr = toUidAbbreviation(documentNameOrAbbr);
  const seq =
    sequence != null && String(sequence).trim() !== ""
      ? String(sequence).trim()
      : "";
  if (!abbr || !seq) return "";
  return `${abbr}-${seq}`;
};

const readSequenceValue = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  const numbered = parseNumberedDocumentUid(
    doc.documentName ?? doc.uid ?? doc.department,
  );
  if (numbered) return numbered.seq;
  const v = doc.sequence ?? doc.sequenceNumber ?? doc.seq ?? doc.number;
  if (v == null || String(v).trim() === "") return "";
  const s = String(v).trim();
  if (/[A-Za-z]/.test(s) || s.includes("-")) return "";
  return s;
};

/** Display/save UID: prefer stored COR-001 style documentName/uid. */
export const resolveDocumentUid = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  const fromName = String(doc.documentName ?? "").trim();
  if (parseNumberedDocumentUid(fromName)) return fromName;
  const fromUid = String(doc.uid ?? "").trim();
  if (parseNumberedDocumentUid(fromUid)) return fromUid;

  const abbr = toUidAbbreviation(doc.documentName ?? doc.department ?? doc.uid);
  const seq = readSequenceValue(doc);
  return buildDocumentUid(abbr, seq) || fromName || fromUid;
};

/** Abbreviation used by an existing register row (COR from COR-001 / CLOSE OUT REPORT). */
export const getDocumentRowAbbreviation = (doc) => {
  if (!doc || typeof doc !== "object") return "";
  return toUidAbbreviation(doc.documentName ?? doc.uid ?? doc.department ?? "");
};

/**
 * Next UID for an abbreviation: COR-001, COR-002, …
 * Scans existing register rows for the same abbreviation.
 */
export const allocateNextDocumentUid = (
  abbreviationOrDescription,
  existingDocs = [],
  excludeId,
) => {
  const abbr = toUidAbbreviation(abbreviationOrDescription);
  if (!abbr) return "";

  let max = 0;
  for (const doc of existingDocs || []) {
    if (
      excludeId != null &&
      String(doc?.id ?? "") !== "" &&
      String(doc.id) === String(excludeId)
    ) {
      continue;
    }
    if (getDocumentRowAbbreviation(doc) !== abbr) continue;
    const seq = readSequenceValue(doc);
    const n = seq ? parseInt(seq, 10) : NaN;
    if (Number.isFinite(n) && n > max) max = n;
  }

  return `${abbr}-${String(max + 1).padStart(3, "0")}`;
};

/**
 * The document's own UID: the identifier's code and nothing else ("BOM").
 *
 * An identifier is used once per WBS revision - one Bill of Materials, one
 * Estimation Summary - so there is no sequence to allocate. Rows written
 * while the register numbered them ("GAD-001") keep the value they hold:
 * resolveDocumentUid still reads those back.
 */
export const ensureDocumentIdentifierUid = (raw) => {
  const incoming = String(
    raw?.documentName ?? raw?.uid ?? raw?.department ?? "",
  ).trim();
  const abbr = toUidAbbreviation(incoming);
  if (!abbr) return { ...raw };

  return { ...raw, documentName: abbr, uid: abbr, sequence: undefined };
};

/**
 * Ensure payload has a numbered UID (GAD-001) in documentName + uid.
 * Keeps existing numbered value when abbreviation is unchanged on edit.
 *
 * Kept for rows that already carry a numbered UID; new ones are named by
 * ensureDocumentIdentifierUid above.
 */
export const ensureNumberedDocumentUid = (
  raw,
  existingDocs = [],
  { excludeId } = {},
) => {
  const incoming = String(raw?.documentName ?? raw?.uid ?? raw?.department ?? "").trim();
  const abbr = toUidAbbreviation(incoming);
  if (!abbr) return { ...raw };

  const incomingNumbered = parseNumberedDocumentUid(incoming);
  const existingRow =
    excludeId != null
      ? (existingDocs || []).find((d) => String(d?.id) === String(excludeId))
      : null;
  const existingUid = resolveDocumentUid(existingRow || {});
  const existingAbbr = getDocumentRowAbbreviation(existingRow || {});

  let uid = "";
  if (incomingNumbered && incomingNumbered.abbr === abbr) {
    uid = `${incomingNumbered.abbr}-${incomingNumbered.seq}`;
  } else if (existingAbbr === abbr && parseNumberedDocumentUid(existingUid)) {
    uid = existingUid;
  } else {
    uid = allocateNextDocumentUid(abbr, existingDocs, excludeId);
  }

  return {
    ...raw,
    documentName: uid,
    uid,
    sequence: parseInt(parseNumberedDocumentUid(uid)?.seq ?? "0", 10) || undefined,
  };
};

/**
 * projectNumber + abbreviation + uid
 * e.g. 1001-PDS-V-101
 */
/** UID for schedule/task rows: direct field or match document register by task code / title. */
export function resolveTaskUidForTask(task, documents = []) {
  if (!task) return "";
  const direct = String(task.uid ?? "").trim();
  if (direct) return direct;
  const key = String(task.taskCode ?? task.task ?? "").trim();
  if (!key) return "";
  const doc = (documents || []).find((d) => {
    const title = String(d?.title ?? d?.document ?? "").trim();
    const docNum = String(d?.docNumber ?? d?.documentNumber ?? "").trim();
    return title === key || docNum === key;
  });
  return doc?.uid != null ? String(doc.uid).trim() : "";
}

/** "0" -> "R0"; an already-tagged "R0" is left as it is. */
export const revisionTag = (revision) => {
  const value = String(revision ?? "").trim();
  if (!value) return "";
  return `R${value.replace(/^[Rr]/, "")}`;
};

/**
 * The register's DUID: PUID-WBS-revision-docUID, e.g.
 * CDT-26001-PRO-001-PV001-R0-Dwg-001. The revision follows the WBS it belongs
 * to, and is left out entirely while the row has none.
 */
export const buildRegisterDocNumber = (doc, { wbsLabel } = {}) => {
  if (!doc || typeof doc !== "object") return "";
  const uid = resolveDocumentUid(doc);
  const parts = [doc.projectNumber, wbsLabel, revisionTag(doc.revision), uid]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  return parts.join("-");
};

/** Merge persisted row with in-progress edit values for live Document # preview. */
export const getRegisterDocSource = (row) => {
  const original = row?.original ?? row ?? {};
  const cache = row?._valuesCache ?? {};
  const documentName =
    cache.documentName ?? original.documentName ?? original.department;
  const uid = resolveDocumentUid({
    ...original,
    ...cache,
    documentName,
    uid: cache.uid ?? original.uid,
  });
  return {
    projectNumber: cache.projectNumber ?? original.projectNumber,
    documentName: uid || documentName,
    uid,
    // Part of the DUID (see buildRegisterDocNumber), so the preview has to
    // follow the Latest Revision field as it is filled in.
    revision: cache.revision ?? original.revision,
  };
};

/** `@IsInt` on CreateDocumentDto — send a JSON number or omit. */
export const normalizeNumberOfSheets = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const n =
    typeof value === "number" ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
};

/**
 * Builds body for POST/PATCH per CreateDocumentDto / UpdateDocumentDto (whitelist-safe).
 * Omits blank optional fields; sends integer `numberOfSheets` when set.
 *
 * `docRefNumber` is the register's "Client Doc" column and `docRevision` its
 * "Client Revision" one - both typed per row, both distinct from the
 * generated documentNumber and from `revision` ("Latest Revision").
 */
export const buildDocumentMutationBody = (
  raw,
  { isCreate, wbsLabel, movedToProjectId } = { isCreate: false },
) => {
  const trim = (s) => (s == null ? "" : String(s).trim());

  const companyName = trim(raw.companyName);
  const projectNumber = trim(raw.projectNumber);

  // Prefer numbered UID already allocated (COR-001); fall back to legacy fields
  const numbered =
    parseNumberedDocumentUid(raw.documentName) ||
    parseNumberedDocumentUid(raw.uid);
  const uid = numbered
    ? `${numbered.abbr}-${numbered.seq}`
    : resolveDocumentUid(raw);
  const documentName = uid || trim(raw.documentName ?? raw.department);

  const body = {};

  if (raw.type != null && String(raw.type).trim() !== "") {
    body.type = String(raw.type).trim();
  }

  const documentNumber = buildRegisterDocNumber(
    {
      projectNumber,
      documentName,
      uid,
      revision: raw.revision,
    },
    { wbsLabel },
  );
  if (documentNumber) body.documentNumber = documentNumber;
  if (companyName) body.companyName = companyName;
  if (projectNumber) body.projectNumber = projectNumber;
  // Persist numbered UID in document identifier field
  if (documentName) body.documentName = documentName;
  if (uid !== "") body.uid = uid;

  const seqFromUid = numbered?.seq ?? readSequenceValue(raw);
  if (seqFromUid !== "") {
    const seqNum = parseInt(String(seqFromUid), 10);
    if (Number.isFinite(seqNum)) {
      body.sequence = seqNum;
      body.number = seqNum;
    } else {
      body.sequence = String(seqFromUid);
    }
  }

  // The fields a row is filled in through. On an edit, one the user cleared
  // is sent as "" - leaving it out would keep the stored value, so a value
  // could never be removed once written. On a create there is nothing to
  // clear, so blanks stay out of the body and the column keeps its default.
  const sendText = (key, value) => {
    const text = trim(value);
    if (text !== "") {
      body[key] = text;
      return;
    }
    if (!isCreate && value !== undefined) body[key] = "";
  };

  sendText("revision", raw.revision);
  sendText("description", raw.description ?? raw.title);
  sendText("docRefNumber", raw.docRefNumber);
  sendText("docRevision", raw.docRevision);

  // Which WBS the row moves to - only when it actually moved. An ordinary
  // edit must not carry it: the register's WBS list is filtered by
  // clientName, so a row whose WBS is missing from that list would send an
  // id the server then refuses with "there's no project with this id",
  // failing an edit that never meant to move anything.
  if (!isCreate && trim(movedToProjectId) !== "") {
    body.projectId = trim(movedToProjectId);
  }

  const numberOfSheets = normalizeNumberOfSheets(raw.numberOfSheets);
  if (numberOfSheets !== undefined) body.numberOfSheets = numberOfSheets;

  const responsible = raw.responsible;
  if (responsible !== undefined && responsible !== null && trim(responsible)) {
    body.responsible = trim(responsible);
  }

  return body;
};

/** Dedupe concurrent document list fetches - a tab and its table can ask at once. */
const documentListInflight = new Map();

const runDeduped = (key, loader) => {
  const existing = documentListInflight.get(key);
  if (existing) return existing;

  const promise = loader().finally(() => {
    if (documentListInflight.get(key) === promise) {
      documentListInflight.delete(key);
    }
  });

  documentListInflight.set(key, promise);
  return promise;
};

const parseMaybeJson = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    return JSON.parse(value) ?? {};
  } catch {
    return {};
  }
};

/** Highest numeric key of a revision table, as its own string ("0", "1", …). */
const highestRevisionKey = (table) => {
  const keys = Object.keys(table || {}).filter(
    (key) => key !== "" && !Number.isNaN(Number(key)),
  );
  if (keys.length === 0) return "";
  return keys.sort((a, b) => Number(a) - Number(b))[keys.length - 1];
};

// A WBS's revisions live in one of two places, by discipline - the same two
// the projects tree reads (see useEmsProjectsTreeData):
//
//   PV          keys of the project's `projectData.all.inputData`, which is
//               what GET project/component/:id/PV/all hands back (the
//               endpoint returns that one key of projectData, nothing more)
//   Estimation  keys of the newest project revision's `estimationTable`
//
// Both ride along in the plant record this page already holds - its projects
// come back whole, revisions and projectData included - so neither costs a
// request here. Which of the two a WBS uses is NOT decided by its stored
// type: that is free-form text no one validates (see projectTypes.js), and a
// WBS carrying none, or a spelling this build doesn't know, would otherwise
// be read from the wrong place and come back with no revision at all. Both
// are read instead, and whichever answers wins.
const latestPvRevision = (project) => {
  const projectData = parseMaybeJson(project?.projectData);
  return highestRevisionKey(parseMaybeJson(projectData?.all)?.inputData);
};

const latestEstimationRevision = (project) => {
  const records = (Array.isArray(project?.revisions) ? project.revisions : [])
    .map((rev) => parseMaybeJson(rev?.data))
    .filter((data) => data && !data.__structuralProjectRevisionDeleted);
  // The record the modules load is the last one, so its table is the set of
  // revisions the WBS actually offers.
  const latest = records[records.length - 1];
  return highestRevisionKey(parseMaybeJson(latest?.estimationTable));
};

// Cached per WBS: the fallbacks below are only reached when the plant record
// has nothing to say (the register in project mode), and the dialog asks
// again every time it opens.
const latestRevisionCache = new Map();

const fetchLatestRevisionDirect = (projectId, pvFirst) => {
  if (latestRevisionCache.has(projectId)) {
    return latestRevisionCache.get(projectId);
  }

  // The same two places, each from the endpoint that serves it: the PV
  // component, and the WBS's revision list as the Estimation modules load it.
  const readPv = () =>
    secondAxios
      .get(`project/component/${projectId}/PV/all`)
      .then((res) => latestPvRevision({ projectData: { all: res.data?.all } }));
  const readEstimation = () =>
    secondAxios
      .get(`/project/${projectId}/rev`)
      .then((res) =>
        latestEstimationRevision({
          revisions: Array.isArray(res.data) ? res.data : [],
        }),
      );

  const promise = (async () => {
    let failed = false;

    for (const read of pvFirst
      ? [readPv, readEstimation]
      : [readEstimation, readPv]) {
      try {
        const revision = await read();
        if (revision) return revision;
      } catch (error) {
        failed = true;
        console.warn("Latest revision load failed:", error);
      }
    }

    // Dropped rather than cached, as fetchPlantRecord does: a load that
    // failed must not leave the field blank for the rest of the session. A
    // WBS that simply has no revisions yet stays cached as the "" it is.
    if (failed) latestRevisionCache.delete(projectId);
    return "";
  })();

  latestRevisionCache.set(projectId, promise);
  return promise;
};

/**
 * The newest revision of one WBS, for the register's "Latest Revision" field.
 *
 * The projects tree passes its own along in the URL when the register is
 * opened from a WBS row, but only once that row has been expanded - and the
 * register is just as often opened on its own. So it is resolved here too,
 * from the WBS the dialog is filling in for.
 *
 * @param {string} projectId  the WBS
 * @param {{ type?: string, plantId?: string }} [scope]  `type` only orders the
 *   two lookups when neither is in hand yet; it is never trusted to rule one out
 * @returns {Promise<string>} "0", "1", … or "" when the WBS has no revisions
 */
export const fetchLatestWbsRevision = async (projectId, scope = {}) => {
  const id = String(projectId ?? "").trim();
  if (!id) return "";

  const { type, plantId } = scope;

  const plant = plantId ? await fetchPlantRecord(plantId) : null;
  const project = (Array.isArray(plant?.projects) ? plant.projects : []).find(
    (candidate) => String(candidate?.id ?? "") === id,
  );
  if (project) {
    const revision = latestPvRevision(project) || latestEstimationRevision(project);
    if (revision !== "") return revision;
  }

  // Nothing in the plant record - the register in project mode, or a WBS its
  // payload left out. Ask for this one WBS instead.
  return fetchLatestRevisionDirect(id, projectTypeDiscipline(type) === "pv");
};

export const invalidateDocumentListCache = () => {
  documentListInflight.clear();
  // The rows live inside the plant record below, so that has to go too.
  plantRecordCache.clear();
};

/**
 * The plant, its WBS, and every WBS's documents - one payload, which is
 * everything this page needs: the table's rows, the WBS picker's options
 * and the project name in the header.
 *
 * It is a heavy payload (each project carries its revisions along), so the
 * readers share one request instead of making four. Cached until a
 * document is written, at which point invalidateDocumentListCache clears
 * it and the next reader re-fetches.
 */
const plantRecordCache = new Map();

export const fetchPlantRecord = (plantId, { force = false } = {}) => {
  const key = String(plantId ?? "").trim();
  if (!key) return Promise.resolve(null);
  if (!force && plantRecordCache.has(key)) return plantRecordCache.get(key);

  const promise = secondAxios
    .get(`/plant/${key}`)
    .then((res) => res.data ?? null)
    .catch((error) => {
      // Dropped rather than cached: a failed load must not stick for the
      // rest of the session.
      plantRecordCache.delete(key);
      console.warn("Plant load failed:", error);
      return null;
    });

  plantRecordCache.set(key, promise);
  return promise;
};

/**
 * Every document under a plant, taken from the plant record - its projects
 * arrive with their documentList attached, so no further request is needed
 * (it used to ask each project for its documents in turn).
 */
const loadPlantDocumentList = async (type, plantId) => {
  const plant = await fetchPlantRecord(plantId);
  const projectsRaw = Array.isArray(plant?.projects) ? plant.projects : [];
  if (projectsRaw.length === 0) return [];

  const client = process.env.REACT_APP_CLIENT_NAME;
  const documentType = type || "all";

  const allDocuments = projectsRaw
    .filter((project) => !client || project?.clientName === client)
    .flatMap((project) =>
      (Array.isArray(project?.documentList) ? project.documentList : [])
        .filter((doc) => documentType === "all" || doc?.type === documentType)
        .map((doc) => ({
          ...doc,
          projectId: project?.id,
          projectName: project?.name,
        })),
    );

  const seen = new Set();
  return allDocuments.filter((doc) => {
    const label =
      doc?.documentNumber ??
      doc?.docNumber ??
      doc?.description ??
      doc?.title ??
      "";
    const key = String(label).toLowerCase().trim();
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
// One WBS's documents - the register in project mode, and the DCS tab.
const fetchDocumentsByProject = async (projectId, type) => {
  if (!projectId) return [];
  const documentType = type || "all";
  const response = (
    await secondAxios.get(`/documents/${projectId}/type/${documentType}`)
  ).data;
  return response;
};

export const getProjectDocumentList = async (type, projectId) => {
  const normalizedType = type || "all";
  const key = `project|${normalizedType}|${projectId ?? ""}`;
  return runDeduped(key, () =>
    fetchDocumentsByProject(projectId, normalizedType),
  );
};

export const getPlantDocumentList = async (type, plantId) => {
  const normalizedType = type || "all";
  const key = `plant|${normalizedType}|${plantId ?? ""}`;
  return runDeduped(key, () => loadPlantDocumentList(normalizedType, plantId));
};

export const updateDocumentList = async (
  updateDocument,
  { wbsLabel, movedToProjectId } = {},
) => {
  const body = buildDocumentMutationBody(updateDocument, {
    isCreate: false,
    wbsLabel,
    movedToProjectId,
  });
  await secondAxios.patch(`/documents/${updateDocument.id}`, body);
  invalidateDocumentListCache();
};

export const createDocumentList = async (
  type,
  projectId,
  newDoc,
  { wbsLabel } = {},
) => {
  const body = buildDocumentMutationBody(
    { ...newDoc, type: newDoc.type ?? type },
    { isCreate: true, wbsLabel },
  );
  await secondAxios.post(`/documents/${projectId}`, body);
  invalidateDocumentListCache();
};

export const deleteDocumentList = async (id) => {
  try {
    await secondAxios.delete(`/documents/${id}`);
    invalidateDocumentListCache();
  } catch (error) {
    console.log(error);
  }
};
